import type { SupabaseClient } from "@supabase/supabase-js";
import {
  isCompleteFrozenServiceWeekReview,
  type FrozenServiceWeekCoverageRow, type FrozenServiceWeekWorkSegment,
  type ServiceWeekCheckpointRow, type ServiceWeekIncidentRow, type ServiceWeekKey,
  type ServiceWeekReceipt, type ServiceWeekSummary, type ServiceWeekTarget,
} from "./service-week-approval";
import {
  CLIENT_ARTIFACT_INTEGRITY, clientArtifactRefusalCopy, isClientArtifactPreparedBy,
  isClientArtifactPrintableLabel as isProofPrintableLabel, type ClientArtifactServiceWeekHeader,
} from "./client-artifact-contract";
export { isClientArtifactPrintableLabel as isProofPrintableLabel } from "./client-artifact-contract";
import { addServiceWeeks, isServiceInstant, isServiceTimeZone, isServiceWeekStart } from "../lib/service-week";

export type ProofOfServiceKey = ServiceWeekKey;
export type ProofOfServiceTarget = ServiceWeekTarget;
export type ProofUnavailableReason = "target_unavailable" | "source_incomplete" | "too_large" | "unavailable";
export type ProofTargetsResult =
  | { status: "ok"; targets: ProofOfServiceTarget[] }
  | { status: "unavailable"; reason: "target_unavailable" | "source_incomplete" | "targets_too_large" | "unavailable" };

export const PROOF_EXCEPTION_SENTENCES = {
  cancelled_after_publish: "Service was cancelled after publication.",
  uncovered: "The scheduled seat has no recorded work.",
  partial: "Only part of the scheduled service was recorded.",
  missing_start: "The start time is missing.",
  missing_end: "The end time is missing.",
  late_start: "Recorded service started after the scheduled start.",
  early_end: "Recorded service ended before the scheduled end.",
  officer_entered: "The time was entered by an officer.",
  supervisor_set: "The time was set by a supervisor.",
  unscheduled: "Unscheduled service was included in the approved week.",
  missed_check_in: "A required check-in was missed.",
  duplicate: "A duplicate check-in was recorded.",
  out_of_window: "A check-in was recorded outside its scheduled window.",
  review_required: "A check-in requires review.",
  offline_recorded: "A check-in was recorded offline.",
  manual_recorded: "A check-in was entered manually.",
  fallback_used: "A check-in used a fallback method.",
  rejected: "A check-in was rejected.",
  out_of_plan: "A check-in was recorded outside the approved plan.",
} as const;
export type ProofExceptionCode = keyof typeof PROOF_EXCEPTION_SENTENCES;
export interface ProofOfServiceException {
  code: ProofExceptionCode; serviceDay: string; serviceLabel: string;
  checkpointLabel: string | null; occurredAt: string | null; sentence: string;
}
export type ProofOfServiceHeader = ClientArtifactServiceWeekHeader;
export interface ProofOfServiceSnapshot {
  header: ProofOfServiceHeader; receipt: ServiceWeekReceipt;
  summary: ServiceWeekSummary & { totalExceptionCount: number };
  coverage: FrozenServiceWeekCoverageRow[]; workSegments: FrozenServiceWeekWorkSegment[];
  checkpoints: ServiceWeekCheckpointRow[]; exceptions: ProofOfServiceException[]; incidents: ServiceWeekIncidentRow[];
  completeness: { coverage: true; exceptions: true; incidentSummary: true };
}
type ProofRefusal = { status: "refused"; reason: "week_unapproved" | "no_scheduled_service" };
type ProofUnavailable = { status: "unavailable"; reason: ProofUnavailableReason };
export type ProofReadResult = { status: "ok"; revision: string; snapshot: ProofOfServiceSnapshot } | ProofRefusal | ProofUnavailable;
export type ProofRevalidationResult = { status: "current" } | { status: "changed" } | ProofRefusal | ProofUnavailable;

export const proofOfServiceCopy = {
  unavailable: clientArtifactRefusalCopy["R-06"]("Proof of service"),
  week_unapproved: clientArtifactRefusalCopy["R-01"]("proof of service"),
  no_scheduled_service: clientArtifactRefusalCopy["R-02"]("proof of service"),
  source_incomplete: clientArtifactRefusalCopy["R-09"]("proof of service"),
  noTargets: clientArtifactRefusalCopy["R-04"]("proof of service"),
  targets_too_large: clientArtifactRefusalCopy["R-05"],
  too_large: clientArtifactRefusalCopy["R-03"],
  changed: "Proof facts changed. Preview the week again.",
  printHint: "The PDF downloads directly with the same pages shown in Preview.",
  laterRounds: "Later · patrol round records are not available yet.",
  integrity: CLIENT_ARTIFACT_INTEGRITY,
  approveInTimesheets: "Approve in Timesheets",
  openInTimesheets: "Open in Timesheets",
} as const;

const SUMMARY_KEYS = ["coverageRowCount", "publishedSeatCount", "coveredSeatCount", "uncoveredSeatCount", "missingBoundaryCount", "exceptionCount", "pendingUnscheduledCount", "includedUnscheduledCount", "rejectedUnscheduledCount", "workSegmentCount", "expectedCheckInCount", "verifiedCheckInCount", "missedCheckInCount", "checkpointFactCount", "checkpointExceptionCount", "incidentCount"] as const;
const HEADER_KEYS = ["providerLabel", "clientLabel", "siteLabel", "timeZone", "weekStart", "weekEndExclusive", "preparedByLabel", "preparedByRole", "generatedAt", "dataThroughAt"] as const;
const SNAPSHOT_KEYS = ["header", "receipt", "summary", "coverage", "workSegments", "checkpoints", "exceptions", "incidents", "completeness"] as const;
const EXCEPTION_KEYS = ["code", "serviceDay", "serviceLabel", "checkpointLabel", "occurredAt", "sentence"] as const;
const unavailable = () => ({ status: "unavailable", reason: "unavailable" } as const);
const record = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v)
  && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null);
const exact = (v: Record<string, unknown>, keys: readonly string[]) => Object.keys(v).length === keys.length && keys.every(key => Object.hasOwn(v, key));
const uuid = (v: unknown): v is string => typeof v === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const revision = (v: unknown): v is string => typeof v === "string" && /^[0-9a-f]{64}$/.test(v);

function keyValid(key: ProofOfServiceKey): boolean {
  return uuid(key.accountId) && uuid(key.clientId) && uuid(key.siteId) && isServiceWeekStart(key.weekStart);
}

/** Presentation-independent reconstruction detects missing, duplicated or altered exception rows. */
export function expectedProofExceptions(facts: Pick<ProofOfServiceSnapshot, "coverage" | "workSegments" | "checkpoints">): ProofOfServiceException[] {
  const out: ProofOfServiceException[] = [];
  const push = (code: ProofExceptionCode, row: FrozenServiceWeekCoverageRow, checkpointLabel: string | null, occurredAt: string | null) => {
    out.push({ code, serviceDay: row.serviceDay, serviceLabel: row.serviceLabel, checkpointLabel, occurredAt, sentence: PROOF_EXCEPTION_SENTENCES[code] });
  };
  const byOrdinal = <T extends { rowOrdinal: number }>(rows: T[]) => [...rows].sort((a, b) => a.rowOrdinal - b.rowOrdinal);
  const segments = new Map<number, FrozenServiceWeekWorkSegment[]>();
  const checkpoints = new Map<number, ServiceWeekCheckpointRow[]>();
  for (const row of byOrdinal(facts.workSegments)) {
    if (!segments.has(row.coverageRowOrdinal)) segments.set(row.coverageRowOrdinal, []);
    segments.get(row.coverageRowOrdinal)!.push(row);
  }
  for (const row of byOrdinal(facts.checkpoints)) {
    if (!checkpoints.has(row.coverageRowOrdinal)) checkpoints.set(row.coverageRowOrdinal, []);
    checkpoints.get(row.coverageRowOrdinal)!.push(row);
  }
  for (const row of byOrdinal(facts.coverage)) {
    for (const code of row.exceptionCodes) push(code, row, null, null);
    for (const segment of segments.get(row.rowOrdinal) ?? []) {
      for (const code of segment.exceptionCodes) push(code, row, null, segment.recordedStart ?? segment.recordedEnd);
    }
    for (const checkpoint of checkpoints.get(row.rowOrdinal) ?? []) {
      if (checkpoint.kind === "recorded_exception") push(checkpoint.exceptionCode, row, checkpoint.checkpointLabel, checkpoint.recordedAt);
      else if (checkpoint.validationStatus === "missing") push("missed_check_in", row, checkpoint.checkpointLabel, null);
    }
  }
  return out;
}

function isSnapshot(value: unknown): value is ProofOfServiceSnapshot {
  if (!record(value) || !exact(value, SNAPSHOT_KEYS) || !record(value.header) || !exact(value.header, HEADER_KEYS)
    || !record(value.summary) || !exact(value.summary, [...SUMMARY_KEYS, "totalExceptionCount"])
    || !record(value.receipt) || !exact(value.receipt, ["approvalState", "approvedAt", "summary"])
    || !record(value.completeness) || !exact(value.completeness, ["coverage", "exceptions", "incidentSummary"])
    || !Object.values(value.completeness).every(flag => flag === true)) return false;
  const h = value.header;
  if (![h.providerLabel, h.clientLabel, h.siteLabel].every(v => isProofPrintableLabel(v))
    || !isClientArtifactPreparedBy(h.preparedByLabel, h.preparedByRole) || !isServiceTimeZone(h.timeZone)
    || !isServiceWeekStart(h.weekStart) || h.weekEndExclusive !== addServiceWeeks(h.weekStart, 1)
    || !isServiceInstant(h.generatedAt) || !isServiceInstant(h.dataThroughAt)
    || value.receipt.approvalState !== "recorded" || !isServiceInstant(value.receipt.approvedAt)
    || h.dataThroughAt !== value.receipt.approvedAt || Date.parse(h.generatedAt) < Date.parse(h.dataThroughAt)) return false;
  const frozen = {
    providerLabel: h.providerLabel, clientLabel: h.clientLabel, siteLabel: h.siteLabel, timeZone: h.timeZone,
    weekStart: h.weekStart, weekEndExclusive: h.weekEndExclusive, basis: "frozen_snapshot",
    summary: value.receipt.summary, coverage: value.coverage, workSegments: value.workSegments, checkpoints: value.checkpoints, incidents: value.incidents,
  };
  const summary = value.summary;
  if (!isCompleteFrozenServiceWeekReview(frozen) || frozen.summary.pendingUnscheduledCount !== 0 || frozen.summary.rejectedUnscheduledCount !== 0
    || !SUMMARY_KEYS.every(key => summary[key] === frozen.summary[key])) return false;
  if (frozen.coverage.some(row => !isProofPrintableLabel(row.serviceLabel) || (row.templateLabel !== null && !isProofPrintableLabel(row.templateLabel)))
    || frozen.checkpoints.some(row => !isProofPrintableLabel(row.routeLabel) || !isProofPrintableLabel(row.checkpointLabel))
    || frozen.incidents.some(row => row.postLabel !== null && !isProofPrintableLabel(row.postLabel))) return false;
  const count = frozen.summary.exceptionCount + frozen.summary.missedCheckInCount + frozen.summary.checkpointExceptionCount;
  if (value.summary.totalExceptionCount !== count || !Array.isArray(value.exceptions) || value.exceptions.length !== count || count > 65000) return false;
  const expected = expectedProofExceptions(frozen);
  return value.exceptions.every((row, index) => record(row) && exact(row, EXCEPTION_KEYS)
    && EXCEPTION_KEYS.every(key => row[key] === expected[index]?.[key]));
}
function refusal(value: Record<string, unknown>): value is ProofRefusal | ProofUnavailable {
  return exact(value, ["status", "reason"]) && (value.status === "refused" && (value.reason === "week_unapproved" || value.reason === "no_scheduled_service")
    || value.status === "unavailable" && ["too_large", "target_unavailable", "source_incomplete", "unavailable"].includes(String(value.reason)));
}
/** Pin the validated object against accidental in-process changes after Preview. */
function freezePrintable<T>(value: T): T {
  if (value !== null && typeof value === "object") {
    for (const child of Object.values(value)) freezePrintable(child);
    Object.freeze(value);
  }
  return value;
}
export function parseProofOfServiceTargets(value: unknown): ProofTargetsResult {
  try {
    if (!record(value)) return unavailable();
    if (value.status === "unavailable" && exact(value, ["status", "reason"])
      && ["target_unavailable", "source_incomplete", "targets_too_large", "unavailable"].includes(String(value.reason))) return value as ProofTargetsResult;
    if (value.status !== "ok" || !exact(value, ["status", "targets"]) || !Array.isArray(value.targets) || value.targets.length > 500) return unavailable();
    const seen = new Set<string>(), clients = new Map<string, string>();
    for (const row of value.targets) {
      if (!record(row) || !exact(row, ["clientId", "clientLabel", "siteId", "siteLabel", "timeZone"])
        || !uuid(row.clientId) || !uuid(row.siteId) || !isProofPrintableLabel(row.clientLabel) || !isProofPrintableLabel(row.siteLabel) || !isServiceTimeZone(row.timeZone)
        || seen.has(row.siteId) || clients.has(row.clientId) && clients.get(row.clientId) !== row.clientLabel) return unavailable();
      seen.add(row.siteId); clients.set(row.clientId, row.clientLabel);
    }
    return value as ProofTargetsResult;
  } catch { return unavailable(); }
}
export function parseProofOfServiceRead(value: unknown): ProofReadResult {
  try {
    if (!record(value)) return unavailable();
    if (refusal(value)) return value;
    return exact(value, ["status", "revision", "snapshot"]) && value.status === "ok" && revision(value.revision) && isSnapshot(value.snapshot)
      ? { status: "ok", revision: value.revision, snapshot: freezePrintable(value.snapshot) } : unavailable();
  } catch { return unavailable(); }
}
export function parseProofOfServiceRevalidation(value: unknown): ProofRevalidationResult {
  try {
    if (!record(value)) return unavailable();
    if (refusal(value)) return value;
    return exact(value, ["status"]) && (value.status === "current" || value.status === "changed") ? { status: value.status } : unavailable();
  } catch { return unavailable(); }
}
export async function fetchProofOfServiceTargets(client: SupabaseClient, accountId: string): Promise<ProofTargetsResult> {
  if (!uuid(accountId)) return unavailable();
  try {
    const { data, error } = await client.rpc("proof_of_service_targets", { p_account_id: accountId });
    return error ? unavailable() : parseProofOfServiceTargets(data);
  } catch { return unavailable(); }
}
const argsFor = (key: ProofOfServiceKey) => ({ p_account_id: key.accountId, p_client_id: key.clientId, p_site_id: key.siteId, p_week_start: key.weekStart });
export async function fetchProofOfServiceWeek(client: SupabaseClient, key: ProofOfServiceKey): Promise<ProofReadResult> {
  if (!keyValid(key)) return unavailable();
  try {
    const { data, error } = await client.rpc("read_proof_of_service_week", argsFor(key));
    const result = error ? unavailable() : parseProofOfServiceRead(data);
    return result.status === "ok" && result.snapshot.header.weekStart !== key.weekStart ? unavailable() : result;
  } catch { return unavailable(); }
}
export async function revalidateProofOfServiceWeek(client: SupabaseClient, key: ProofOfServiceKey, expectedRevision: string): Promise<ProofRevalidationResult> {
  if (!keyValid(key) || !revision(expectedRevision)) return unavailable();
  try {
    const { data, error } = await client.rpc("revalidate_proof_of_service_week", { ...argsFor(key), p_expected_revision: expectedRevision });
    return error ? unavailable() : parseProofOfServiceRevalidation(data);
  } catch { return unavailable(); }
}
